import {Component, EventEmitter, Input, OnInit, Output, OnDestroy, Renderer2} from '@angular/core';
import {PhotoData} from '../employee-badge-requests';
import * as EmployeeModels from '@employee-models/index';
import {IBadgeRequestPhotoInfo} from '@employee-models/data.models';
import {CommonConfig} from '@employee-configs/common.config';
import {BadgeRequestsService} from 'DBadgeEmployee-app/src/app/core/services/data-access/badge-requests.service';
import {debounceTime, map, take} from 'rxjs/operators';
import {ResponsiveService} from '@ui-app/services/responsive.service';
import {BaseComponent} from '@ui-app/components/base.component';
import {ApiConfig} from '@employee-configs/api.config';
import { Observable, fromEvent, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {SpinnerService} from '@ui-app/components/spinner/spinner.service';
import {EmployeeBusinessService} from 'DBadgeEmployee-app/src/app/core/services/business.service';
import {validatePhotoQuality} from '@ui-app/utils/photo-quality.utils';

@Component({
    selector: 'photo-compliance',
    templateUrl: './photo-compliance.component.html'
})
export class PhotoComplianceComponent extends BaseComponent implements OnInit, OnDestroy {

    @Input() userCanTakePhoto = true;
    @Input() canUseCurrentImage: boolean;
    @Input() photoData: PhotoData;
    @Input() userPhotoUrl: string = null;
    @Input() newFileName: string = null;
    @Input() requestData: EmployeeModels.DM.IBadgeRequestInfoEmployee;
    @Input() isVisitorRequest = false;

    @Output() onUpload = new EventEmitter<IBadgeRequestPhotoInfo>();
    @Output() onUseCurrentPhoto = new EventEmitter<IBadgeRequestPhotoInfo>();
    @Output() onDelete = new EventEmitter<void>();

    readonly uploadConfig = {
        ...CommonConfig.fileUploadRestrictions.photo,
        areaTitle: 'Drag & Drop photo here or',
        customRestrictions: 'Provide a photo that meets all mentioned requirements',
        errors: {
            filesCount: 'Only one file can be uploaded',
        },
    };
    readonly cameraConfig = CommonConfig.cameraConfig;
    isDesktop: boolean;
    resizeSubscription: Subscription;

    constructor(
        private badgeRequestsService: BadgeRequestsService,
        private responsiveService: ResponsiveService,
        private toastr: ToastrService,
         private spinnerService: SpinnerService,
        private renderer: Renderer2,
        private businessService: EmployeeBusinessService
    ) {
        super();
    }

    ngOnInit(): void {
        this.responsiveService.isDesktop.pipe(take(1)).subscribe(data => this.isDesktop = data);
        this.checkResolutionAndShowToast();
        this.resizeSubscription = fromEvent(window, 'resize')
        .pipe(debounceTime(800)) 
        .subscribe(() => this.checkResolutionAndShowToast());
    }

    ngOnDestroy(): void {
        if (this.resizeSubscription) {
            this.resizeSubscription.unsubscribe();
        }
    }

    checkResolutionAndShowToast(): void {
        if (window.matchMedia('(max-width: 767.9px)').matches) {
            this.renderer.addClass(document.body, 'mobile-view');
            this.toastr.info('Use desktop view for better experience', 'Info:');
        } else {
            this.renderer.removeClass(document.body, 'mobile-view');
        }
    }

    onPhotoTaken(event: PhotoData): void {
        this.photoData = event;
        if (this.photoData.url) {
            this.userPhotoUrl = this.photoData.url;
        }

        if (!!this.photoData.uId) {
            if (this.photoData.uId && !this.photoData.url) {
                this.userPhotoUrl = `${ApiConfig.File.fileUrl}/${this.photoData.uId}`;
            }
            this.photoData.url = this.userPhotoUrl;
            const file = this.getFileFromPhotoData(this.photoData);
            this.onUpload.emit(file);
        }
    }

    deletePhoto(): void {
        this.photoData = null;
        this.userPhotoUrl = '';
        this.onDelete.emit();
    }

    useCurrentPhoto(): void {
        const userUid = this.requestData.lastApprovedBadgeImageUid;
        this.photoData = {
            uId: userUid
        };
        console.log("useCurrentPhoto");
        console.log(userUid);
        let request: Observable<EmployeeModels.DM.IDownloadUrlResponse>;
        if (this.isVisitorRequest) {
            request = this.badgeRequestsService.getVisitorDownloadUrl(userUid);
        } else {
            request = this.badgeRequestsService.getDownloadUrl(userUid);
        }

        this.subs = request.pipe(
            map(result => {
                this.userPhotoUrl = result.url;
                const currentPhotoData = { uId: userUid, ...result };
                return this.getFileFromPhotoData(currentPhotoData);
            })
        ).subscribe(file => this.onUseCurrentPhoto.emit(file));
    }

 onPhotoUploaded(event: PhotoData): void {
    this.spinnerService.next(true);
    this.photoData = event;

    // Check photo quality
    const image = new Image();
     
    image.crossOrigin = "Anonymous";
    image.src = this.photoData.url;

    image.onload = () => {
        const currentUser = this.businessService.userProfileService.user;
        if (!currentUser.isUSI) {
            const result = validatePhotoQuality(image);
            if (!result.isValid) {
                this.toastr.error(result.errorMessage, 'Error:');
                this.spinnerService.next(false);
                this.deletePhoto();
                return;
            }
            this.spinnerService.next(false);
        }
        // If photo quality is acceptable, proceed with upload
        const file = this.getFileFromPhotoData(this.photoData);
        this.onUpload.emit(file);
    };
}

    getFileFromPhotoData(photoData: PhotoData): IBadgeRequestPhotoInfo {
        const { uId, contentType, length, url } = photoData;
        return {
            uId,
            name: this.newFileName,
            contentType,
            length,
            url,
            documentType: EmployeeModels.DE.DocumentTypeEnum.FrontFace
        };
    }
}